import { format } from 'date-fns';
import { DownloadIcon } from 'lucide-react';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import { colors } from '../theme/colors';
import {
  customerReportService,
  type GroupReportEmployee,
  type GroupReportResponse,
} from '../services/customerReportService';
import { exportGroupReportToPDF } from '../utils/groupReportExports';

interface GroupReportPreviewProps {
  reportData: GroupReportResponse;
  selectedEmployees: GroupReportEmployee[];
  previewTotals: {
    totalBillableHours: number;
    totalTravelTimeBillable: number;
    totalTurnover: number;
  };
  canViewSalesRate: boolean;
  projectNumber: string;
  projectName: string;
  month: string;
  selectedOrder?: string;
  customOrderNumber?: string;
}

const GERMAN_MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const formatMonthYear = (monthYear: string): string => {
  const [year, month] = monthYear.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  return `${GERMAN_MONTH_NAMES[monthIndex]} ${year}`;
};

export const GroupReportPreview: React.FC<GroupReportPreviewProps> = ({
  reportData,
  selectedEmployees,
  previewTotals,
  canViewSalesRate,
  projectNumber,
  projectName,
  month,
  selectedOrder = '',
  customOrderNumber = '',
}) => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const orderDisplay = useMemo(() => {
    if (customOrderNumber && customOrderNumber.trim() !== '') {
      return customOrderNumber;
    }
    if (!selectedOrder || selectedOrder === '') {
      return '-';
    }
    if (selectedOrder === '__NOT_APPLICABLE__') {
      return '-';
    }
    return selectedOrder;
  }, [selectedOrder, customOrderNumber]);

  const estimateRowHeight = (): number => {
    const baseRowHeight = 27;
    const cellPaddingVertical = 8;
    return baseRowHeight + cellPaddingVertical * 2;
  };

  const calculatePages = useMemo(() => {
    if (selectedEmployees.length === 0) {
      return [];
    }

    const mmToPx = 3.779527559;
    const pageHeight = 297;
    const pageHeightPx = pageHeight * mmToPx;

    const headerHeightPx = 120;
    const tableHeaderHeightPx = 30;
    const signaturesHeightPx = 100;
    const footerHeightPx = 20;
    const totalRowHeightPx = 40;
    const contentPadding = 24;

    const firstPageAvailableHeight = pageHeightPx - headerHeightPx - tableHeaderHeightPx - contentPadding - signaturesHeightPx - footerHeightPx - 20;
    const otherPageAvailableHeight = pageHeightPx - tableHeaderHeightPx - contentPadding - 10;

    const pages: GroupReportEmployee[][] = [];
    let currentPageRows: GroupReportEmployee[] = [];
    let currentPageHeight = firstPageAvailableHeight;

    selectedEmployees.forEach((employee, index) => {
      const entryHeight = estimateRowHeight();

      const remainingEntries = selectedEmployees.length - index - 1;
      const willBeLastPage = remainingEntries === 0;

      const neededReserved = willBeLastPage ? signaturesHeightPx + footerHeightPx + totalRowHeightPx : 0;

      const spaceNeeded = entryHeight + neededReserved;
      const hasEnoughSpace = currentPageHeight >= spaceNeeded;

      if (!hasEnoughSpace && currentPageRows.length > 0) {
        pages.push([...currentPageRows]);
        currentPageRows = [];
        currentPageHeight = otherPageAvailableHeight;

        const newNeededReserved = willBeLastPage ? signaturesHeightPx + footerHeightPx + totalRowHeightPx : 0;
        const newSpaceNeeded = entryHeight + newNeededReserved;

        if (currentPageHeight >= newSpaceNeeded) {
          currentPageRows.push(employee);
          currentPageHeight -= entryHeight;
          if (willBeLastPage) {
            currentPageHeight -= newNeededReserved;
          }
        } else {
          currentPageRows.push(employee);
          currentPageHeight -= entryHeight;
        }
      } else {
        currentPageRows.push(employee);
        currentPageHeight -= entryHeight;
        if (willBeLastPage) {
          currentPageHeight -= neededReserved;
        }
      }
    });

    if (currentPageRows.length > 0) {
      pages.push(currentPageRows);
    }

    return pages;
  }, [selectedEmployees]);

  const handleExportPDF = async () => {
    if (!pdfContainerRef.current || calculatePages.length === 0) {
      return;
    }
    const projectData = {
      projectNumber,
      month: formatMonthYear(month),
    };
    await exportGroupReportToPDF(pdfContainerRef, projectData, setIsExporting);
  };

  const handleExportExcel = async () => {
    const orderParam = customOrderNumber?.trim() ? customOrderNumber : selectedOrder ?? '';
    setIsExportingExcel(true);
    try {
      await customerReportService.exportGroupReportExcel(projectNumber, month, orderParam || undefined);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {calculatePages.length > 0 && (
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button
            onClick={handleExportExcel}
            disabled={isExportingExcel}
            style={{
              padding: '0.625rem 1.25rem',
              height: '2.5rem',
              backgroundColor: isExportingExcel ? colors.neutral.grey[400] : colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: isExportingExcel ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            }}
          >
            {isExportingExcel ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${colors.neutral.white}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Excel wird erstellt...
              </>
            ) : (
              <>Als Excel exportieren</>
            )}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            style={{
              padding: '0.625rem 1.25rem',
              height: '2.5rem',
              backgroundColor: isExporting ? colors.neutral.grey[400] : colors.accent.blue,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            }}
            onMouseEnter={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = colors.accent.blueHover;
                e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = colors.accent.blue;
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)';
              }
            }}
          >
            {isExporting ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${colors.neutral.white}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                PDF wird erstellt...
              </>
            ) : (
              <>
                <DownloadIcon size={18} />
                Als PDF exportieren
              </>
            )}
          </button>
        </div>
      )}
      <div
        ref={pdfContainerRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          padding: '2rem 0',
          gap: '2rem',
        }}
      >
        {calculatePages.map((pageEmployees, pageIndex) => {
        const isLastPage = pageIndex === calculatePages.length - 1;
        const globalStartIndex = calculatePages.slice(0, pageIndex).reduce((sum, p) => sum + p.length, 0);

        return (
          <div
            key={pageIndex}
            data-pdf-export="true"
            style={{
              width: '210mm',
              height: '297mm',
              backgroundColor: colors.neutral.white,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              padding: '0',
              margin: '0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                backgroundColor: '#9DB4C0',
                padding: '2rem',
                borderRadius: '0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <img
                    src="/assets/logo/PPE-intelligence_Logo_rgb_Black.svg"
                    alt="PPF Intelligence Logo"
                    style={{ height: '40px', marginBottom: '0.5rem' }}
                  />
                  <h1 style={{ color: 'white', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                    Kundenbericht - Gruppennachweis
                  </h1>
                </div>
                <div style={{ color: 'white', textAlign: 'right', fontSize: '0.875rem' }}>
                  <div style={{ marginBottom: '0.375rem' }}>
                    <span style={{ opacity: 0.8 }}>Projektname:</span> <strong>{projectName}</strong>
                  </div>
                  <div style={{ marginBottom: '0.375rem' }}>
                    <span style={{ opacity: 0.8 }}>Bestellnummer:</span> <strong>{orderDisplay}</strong>
                  </div>
                  <div>
                    <span style={{ opacity: 0.8 }}>Monat:</span> <strong>{formatMonthYear(month)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              <div style={{ overflow: 'hidden', flex: 1, minHeight: 0 }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                  }}
                >
                  <thead
                    style={{
                      display: 'table-header-group',
                      position: 'relative',
                    }}
                  >
                    <tr style={{ borderBottom: `2px solid ${colors.neutral.grey[300]}` }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                        Mitarbeiter Name
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                        Billable h
                      </th>
                      {reportData.include_travel_time && (
                        <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                          Travel Time Billable h
                        </th>
                      )}
                      <th style={{ textAlign: 'center', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                        Worktype
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                        Verkaufssatz (EUR)
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                        Umsatz {reportData.include_travel_time ? '(inklusive Travel h)' : ''} (EUR)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageEmployees.map((employee, localIndex) => {
                      const globalIndex = globalStartIndex + localIndex;
                      
                      return (
                        <tr
                          key={`${employee.employee_id}-${globalIndex}`}
                          style={{
                            borderBottom: `1px solid ${colors.neutral.grey[200]}`,
                          }}
                        >
                          <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{employee.employee_name}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                            {employee.billable_hours.toFixed(2)}
                          </td>
                          {reportData.include_travel_time && (
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                              {employee.travel_time_billable.toFixed(2)}
                            </td>
                          )}
                          <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.7rem',
                                backgroundColor: employee.worktype === 'office' ? colors.accent.blue + '20' : colors.accent.green + '20',
                                color: employee.worktype === 'office' ? colors.accent.blue : colors.accent.green,
                              }}
                            >
                              {employee.worktype.toUpperCase() || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                            {canViewSalesRate ? `€${employee.sales_rate.toFixed(2)}` : 'N/A'}
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                            €{employee.turnover.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {isLastPage && (
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${colors.neutral.grey[300]}` }}>
                        <td style={{ padding: '0.5rem', fontSize: '0.75rem', fontWeight: '600' }}>Total</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                          {previewTotals.totalBillableHours.toFixed(2)}
                        </td>
                        {reportData.include_travel_time && (
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                            {previewTotals.totalTravelTimeBillable.toFixed(2)}
                          </td>
                        )}
                        <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem' }}></td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}></td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                          €{previewTotals.totalTurnover.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {isLastPage && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '1.5rem',
                    marginTop: 'auto',
                    paddingTop: '5rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div>
                    <div
                      style={{
                        borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                        paddingBottom: '0.5rem',
                        marginBottom: '0.5rem',
                        height: '48px',
                      }}
                    />
                    <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Projektleiter</div>
                  </div>
                  <div>
                    <div
                      style={{
                        borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                        paddingBottom: '0.5rem',
                        marginBottom: '0.5rem',
                        height: '48px',
                      }}
                    />
                    <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Kunde</div>
                  </div>
                </div>
              )}

              <div
                style={{
                  marginTop: isLastPage ? '0.5rem' : 'auto',
                  paddingTop: '0.5rem',
                  borderTop: `1px solid ${colors.neutral.grey[200]}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.5rem',
                  color: colors.neutral.grey[500],
                }}
              >
                <span>Seite {pageIndex + 1}/{calculatePages.length}</span>
                <span>Erstellt am: {format(new Date(), 'dd.MM.yyyy HH:mm')} Uhr</span>
              </div>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};
